<template>
  <div class="class-summary-page">
    <!-- Header toolbar -->
    <v-layout row wrap align-center class="px-4 pt-4 pb-0">
      <v-flex>
        <div class="display-1 font-weight-bold class-summary-title">
          <v-icon large color="teal darken-1" class="mr-2"
            >mdi-chair-school</v-icon
          >
          Class Summary
        </div>
      </v-flex>
      <v-flex shrink>
        <v-btn
          color="teal darken-1"
          outline
          small
          :loading="loading"
          @click="loadSummary"
        >
          <v-icon left small>mdi-refresh</v-icon>
          Refresh
        </v-btn>
      </v-flex>
    </v-layout>

    <!-- Total badge bar -->
    <v-layout row wrap align-center class="px-4 pt-0 pb-3">
      <v-flex shrink class="mr-0">
        <v-chip
          color="teal darken-1"
          text-color="white"
          class="stat-chip font-weight-bold"
        >
          <v-icon left small color="white">mdi-account-group</v-icon>
          {{ totalStudents }} Total Students
        </v-chip>
      </v-flex>
      <v-flex shrink>
        <v-chip
          color="blue-grey darken-1"
          text-color="white"
          class="stat-chip font-weight-bold"
        >
          <v-icon left small color="white">mdi-chair-school</v-icon>
          {{ filteredClasses.length }} Classes
        </v-chip>
      </v-flex>
      <v-spacer></v-spacer>
      <v-flex shrink v-if="showBranchFilter" class="branch-filter-area">
        <!-- Few branches (≤4): draggable selected + unselected toggle chips -->
        <template v-if="availableBranches.length <= 4">
          <draggable
            v-model="selectedBranches"
            class="branch-drag-area"
            :options="{ animation: 150, ghostClass: 'branch-chip-ghost' }"
          >
            <v-chip
              v-for="branch in selectedBranches"
              :key="branch"
              color="teal darken-1"
              text-color="white"
              class="mr-1 branch-chip drag-chip"
              close
              @input="removeBranch(branch)"
            >
              {{ branch }}
            </v-chip>
          </draggable>
          <v-chip
            v-for="branch in unselectedBranches"
            :key="branch"
            color="grey lighten-2"
            text-color="grey darken-2"
            class="mr-1 branch-chip"
            @click="addBranch(branch)"
          >
            {{ branch }}
          </v-chip>
        </template>

        <!-- Many branches (>4): draggable selected chips + dropdown to add more -->
        <template v-else>
          <draggable
            v-model="selectedBranches"
            class="branch-drag-area"
            :options="{ animation: 150, ghostClass: 'branch-chip-ghost' }"
          >
            <v-chip
              v-for="branch in selectedBranches"
              :key="branch"
              color="teal darken-1"
              text-color="white"
              class="mr-1 branch-chip drag-chip"
              close
              @input="removeBranch(branch)"
            >
              {{ branch }}
            </v-chip>
          </draggable>

          <v-menu
            v-model="branchMenuOpen"
            :close-on-content-click="false"
            bottom
            left
            max-height="320"
          >
            <v-btn
              slot="activator"
              icon
              small
              flat
              class="branch-add-btn"
              title="Add branch"
            >
              <v-icon>mdi-plus-circle-outline</v-icon>
            </v-btn>
            <v-card class="pa-2" style="min-width: 200px;">
              <v-text-field
                v-model="branchSearch"
                placeholder="Search branches..."
                prepend-inner-icon="mdi-magnify"
                hide-details
                single-line
                class="pt-0 mb-1"
                autofocus
              ></v-text-field>
              <v-list dense style="max-height: 220px; overflow-y: auto;">
                <v-list-tile
                  v-for="branch in filteredUnselectedBranches"
                  :key="branch"
                  @click="addBranch(branch)"
                  class="branch-menu-item"
                >
                  <v-list-tile-title>{{ branch }}</v-list-tile-title>
                </v-list-tile>
                <v-list-tile
                  v-if="filteredUnselectedBranches.length === 0"
                  disabled
                >
                  <v-list-tile-title class="grey--text"
                    >No results</v-list-tile-title
                  >
                </v-list-tile>
              </v-list>
            </v-card>
          </v-menu>
        </template>
      </v-flex>
    </v-layout>

    <v-alert :value="!!errorMessage" type="error" @input="errorMessage = ''">
      {{ errorMessage }}
    </v-alert>

    <!-- Loading skeleton -->
    <v-layout row wrap class="px-4" v-if="loading">
      <v-flex
        xs12
        sm6
        md4
        lg3
        v-for="n in 8"
        :key="'skeleton-' + n"
        class="pa-2"
      >
        <v-card class="class-card skeleton-card" height="180">
          <v-card-text>
            <div class="skeleton-line skeleton-short mb-3"></div>
            <div class="skeleton-line skeleton-full mb-2"></div>
            <div class="skeleton-line skeleton-medium mb-2"></div>
            <div class="skeleton-count mt-3"></div>
          </v-card-text>
        </v-card>
      </v-flex>
    </v-layout>

    <!-- Branch groups -->
    <div v-if="!loading">
      <div
        v-for="(classGroups, branchName) in groupedClasses"
        :key="branchName"
        class="mb-5"
      >
        <!-- Branch header -->
        <v-layout row wrap align-center class="px-4 pb-1 pt-2">
          <v-flex>
            <div class="title font-weight-bold branch-header">
              <v-icon class="mr-1" color="teal darken-2">mdi-domain</v-icon>
              {{ branchName }}
              <v-chip
                small
                color="teal lighten-4"
                text-color="teal darken-3"
                class="ml-2"
              >
                {{ branchTotals[branchName] }} students
              </v-chip>
            </div>
          </v-flex>
        </v-layout>

        <!-- Class sub-groups -->
        <div
          v-for="(items, className) in classGroups"
          :key="branchName + '-' + className"
          class="class-subgroup"
        >
          <!-- Class sub-header -->
          <div class="class-subheader px-5">
            <v-icon x-small class="mr-1" color="blue-grey lighten-1">mdi-chair-school</v-icon>
            {{ className }}
          </div>

          <v-layout row wrap class="px-2">
            <v-flex
              xs12
              sm6
              md4
              lg3
              v-for="(item, idx) in items"
              :key="branchName + '-' + className + '-' + item.roomNumber + '-' + idx"
              class="pa-2"
            >
              <v-card
                class="class-card"
                :class="getCardClass(item.studentCount)"
                hover
              >
                <!-- Count badge -->
                <div
                  class="count-badge"
                  :class="getCountBadgeClass(item.studentCount)"
                >
                  <span class="count-number">{{ item.studentCount }}</span>
                  <span class="count-label">students</span>
                </div>

                <v-card-title class="pb-0 pt-3 card-title-block">
                  <div class="room-number-text" v-if="item.roomNumber">
                    <v-icon small class="mr-1 info-icon">mdi-door</v-icon>
                    Room {{ item.roomNumber }}
                  </div>
                  <div v-else class="room-number-text no-room">—</div>
                </v-card-title>

                <v-card-text class="pt-2 pb-3">
                  <div v-if="item.teacherH" class="info-row">
                    <v-icon small class="mr-1 info-icon">mdi-account</v-icon>
                    <span class="ml-1">{{ item.teacherH }}</span>
                  </div>
                  <div v-if="item.teacherH2" class="info-row mt-1">
                    <v-icon small class="mr-1 info-icon">mdi-account</v-icon>
                    <span class="ml-1">{{ item.teacherH2 }}</span>
                  </div>
                </v-card-text>
              </v-card>
            </v-flex>
          </v-layout>
        </div>
      </div>

      <!-- Empty state -->
      <v-layout
        row
        wrap
        justify-center
        class="mt-5"
        v-if="classes.length === 0"
      >
        <v-flex xs12 sm6 md4 class="text-xs-center">
          <v-icon size="80" color="grey lighten-2">mdi-chair-school</v-icon>
          <div class="title grey--text mt-3">No classes found</div>
          <div class="subheading grey--text">
            There is no class data available for this term.
          </div>
        </v-flex>
      </v-layout>
    </div>
  </div>
</template>

<script>
import draggable from "vuedraggable";

export default {
  components: { draggable },
  data: () => ({
    classes: [],
    loading: false,
    errorMessage: "",
    selectedBranches: [],
    userBranch: "",
    branchMenuOpen: false,
    branchSearch: "",
    refreshIntervalID: null,
  }),

  computed: {
    showBranchFilter() {
      return this.userBranch === "null" || !this.userBranch;
    },

    availableBranches() {
      const branches = [];
      this.classes.forEach(function(c) {
        if (c.branch && branches.indexOf(c.branch) === -1) {
          branches.push(c.branch);
        }
      });
      return branches;
    },

    filteredClasses() {
      return this.classes.filter((c) => {
        const section = c.section;
        if (section === "0") return false;
        if (section && section.toUpperCase().indexOf("X") !== -1) return false;
        if (this.selectedBranches && this.selectedBranches.length > 0) {
          return this.selectedBranches.indexOf(c.branch) !== -1;
        }
        return true;
      });
    },

    groupedClasses() {
      const unordered = {};
      this.filteredClasses.forEach(function(c) {
        const branch = c.branch || "Unknown";
        const cls = c.class || "—";
        if (!unordered[branch]) {
          unordered[branch] = {};
        }
        if (!unordered[branch][cls]) {
          unordered[branch][cls] = [];
        }
        unordered[branch][cls].push(c);
      });

      // Re-insert keys in selectedBranches order, then any remaining
      const ordered = {};
      const branchOrder = this.selectedBranches.length > 0
        ? this.selectedBranches.concat(
            Object.keys(unordered).filter(b => this.selectedBranches.indexOf(b) === -1)
          )
        : Object.keys(unordered);

      branchOrder.forEach(function(branch) {
        if (unordered[branch]) {
          ordered[branch] = unordered[branch];
        }
      });

      return ordered;
    },

    branchTotals() {
      const totals = {};
      this.filteredClasses.forEach(function(c) {
        const key = c.branch || "Unknown";
        totals[key] = (totals[key] || 0) + (c.studentCount || 0);
      });
      return totals;
    },

    totalStudents() {
      return this.filteredClasses.reduce(function(sum, c) {
        return sum + (c.studentCount || 0);
      }, 0);
    },

    unselectedBranches() {
      return this.availableBranches.filter(
        (b) => this.selectedBranches.indexOf(b) === -1
      );
    },

    filteredUnselectedBranches() {
      const search = this.branchSearch && this.branchSearch.toLowerCase();
      if (!search) return this.unselectedBranches;
      return this.unselectedBranches.filter(
        (b) => b.toLowerCase().indexOf(search) !== -1
      );
    },
  },

  async created() {
    this.userBranch = this.$store.state.authentication.branch;
    await this.loadSummary();

    if (this.showBranchFilter) {
      const defaults = ["SEL", "SEL6"].filter(
        (b) => this.availableBranches.indexOf(b) !== -1
      );
      this.selectedBranches = defaults.length > 0 ? defaults : [];
    }

    this.refreshIntervalID = setInterval(() => {
      this.loadSummary();
    }, 10 * 60 * 1000);
  },

  beforeDestroy() {
    clearInterval(this.refreshIntervalID);
  },

  methods: {
    async loadSummary() {
      this.loading = true;
      this.errorMessage = "";

      try {
        const branch =
          this.userBranch && this.userBranch !== "null"
            ? this.userBranch
            : null;

        const url =
          "api/allStudents/GetClassSummary" +
          (branch ? "?branch=" + encodeURIComponent(branch) : "");

        this.classes = await this.$http.get(url);
      } catch (e) {
        this.errorMessage = "Failed to load class summary. Please try again.";
      }

      this.loading = false;
    },

    toggleBranch(branch) {
      const idx = this.selectedBranches.indexOf(branch);
      if (idx === -1) {
        this.selectedBranches.push(branch);
      } else {
        this.selectedBranches.splice(idx, 1);
      }
    },

    addBranch(branch) {
      if (this.selectedBranches.indexOf(branch) === -1) {
        this.selectedBranches.push(branch);
      }
      this.branchSearch = "";
      if (this.unselectedBranches.length === 0) {
        this.branchMenuOpen = false;
      }
    },

    removeBranch(branch) {
      const idx = this.selectedBranches.indexOf(branch);
      if (idx !== -1) {
        this.selectedBranches.splice(idx, 1);
      }
    },

    getCardClass(count) {
      if (count >= 30) return "card-full";
      if (count >= 20) return "card-busy";
      if (count >= 10) return "card-moderate";
      return "card-light";
    },

    getCountBadgeClass(count) {
      if (count >= 30) return "badge-red";
      if (count >= 20) return "badge-orange";
      if (count >= 10) return "badge-teal";
      return "badge-green";
    },
  },
};
</script>

<style scoped>
.class-summary-page {
  background-color: #f5f6fa;
  min-height: 100vh;
  padding-bottom: 40px;
}

.class-summary-title {
  color: #00695c;
  display: flex;
  align-items: center;
}

.branch-header {
    color: #37474f;
    border-left: 4px solid #00897b;
    padding-left: 10px;
}

.class-subgroup {
    margin-top: 4px;
}

.class-subheader {
    display: inline-flex;
    align-items: center;
    font-size: 0.8rem;
    font-weight: 700;
    color: #78909c;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    padding-top: 6px;
    padding-bottom: 2px;
}

/* Base card */
.class-card {
  border-radius: 12px !important;
  position: relative;
  overflow: hidden;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  border-top: 4px solid transparent;
}

.class-card:hover {
  transform: translateY(-3px);
}

.card-full {
  border-top-color: #e53935;
}
.card-busy {
  border-top-color: #fb8c00;
}
.card-moderate {
  border-top-color: #00897b;
}
.card-light {
  border-top-color: #43a047;
}

.card-title-block {
  display: flex !important;
  flex-direction: column !important;
  align-items: flex-start !important;
}

.room-number-text {
    font-size: 1.2rem;
    font-weight: 800;
    color: #00695c;
    display: flex;
    align-items: center;
    width: calc(100% - 70px);
}

.room-number-text.no-room {
    color: #b0bec5;
    font-weight: 400;
}


/* Count badge (top-right corner) */
.count-badge {
  position: absolute;
  top: 10px;
  right: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  border-radius: 10px;
  padding: 4px 10px;
  min-width: 56px;
}

.badge-red {
  background-color: #ffebee;
  border: 1.5px solid #e53935;
}
.badge-orange {
  background-color: #fff3e0;
  border: 1.5px solid #fb8c00;
}
.badge-teal {
  background-color: #e0f2f1;
  border: 1.5px solid #00897b;
}
.badge-green {
  background-color: #e8f5e9;
  border: 1.5px solid #43a047;
}

.count-number {
  font-size: 1.5rem;
  font-weight: 800;
  line-height: 1.2;
  color: #263238;
}

.count-label {
  font-size: 0.65rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #607d8b;
}

/* Info rows */
.info-row {
  display: flex;
  align-items: center;
  font-size: 0.85rem;
}

.info-icon {
  color: #90a4ae !important;
}

/* Skeleton loading */
.skeleton-card {
  background-color: #fff;
}

.skeleton-line {
  height: 12px;
  border-radius: 6px;
  background: linear-gradient(90deg, #eceff1 25%, #e0e0e0 50%, #eceff1 75%);
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite;
}

.skeleton-short {
  width: 40%;
}
.skeleton-full {
  width: 90%;
}
.skeleton-medium {
  width: 65%;
}

.skeleton-count {
  width: 56px;
  height: 56px;
  border-radius: 10px;
  float: right;
  background: linear-gradient(90deg, #eceff1 25%, #e0e0e0 50%, #eceff1 75%);
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite;
}

@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

.stat-chip .v-chip__content {
  padding: 0 10px;
}

.stat-chip .v-icon {
  color: white !important;
  margin-right: 6px !important;
  margin-left: 0 !important;
}

.branch-chip {
    cursor: pointer !important;
}

.branch-chip .v-chip__content {
    cursor: pointer !important;
}

.drag-chip {
    cursor: grab !important;
}

.drag-chip .v-chip__content {
    cursor: grab !important;
}

.drag-chip:active {
    cursor: grabbing !important;
}

.branch-drag-area {
    display: inline-flex;
    align-items: center;
    flex-wrap: wrap;
}

.branch-chip-ghost {
    opacity: 0.4;
}

.branch-filter-area {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
}

.branch-add-btn {
  cursor: pointer !important;
}

.branch-menu-item {
  cursor: pointer !important;
}
</style>
